import React, { useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";
import {
  PostReservationSegmentConfig,
  PutApplyReservationTemplate,
} from "api/rpc/2024-04/facilityAdmin/reservation/reservation";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { ITemplate } from "redux/reducers/models/reservations";

import Sheet from "components/sheet/Sheet";
import SelectNew from "components/select/SelectNew";
import TimePick from "components/timePick/TimePick";
import ButtonGroup from "components/buttonGroup/ButtonGroup";

import "./reservationCalendarDay.scss";

const intervalOptions = [30, 60];

interface IReservationCalendarDayProps {
  open: boolean;
  date: string;
  templates: ITemplate[];
  onClose: () => void;
  onApplied: () => void;
  onGenerated: () => void;
}

export default function ReservationCalendarDay(props: IReservationCalendarDayProps) {
  const { open, date, templates, onClose, onApplied, onGenerated } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [mode, setMode] = useState<"template" | "generate">("template");
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [startTime, setStartTime] = useState("07:00");
  const [endTime, setEndTime] = useState("19:30");
  const [interval, setInterval] = useState(30);

  async function applyTemplate() {
    if (!templateId) {
      dispatch(showError(t("Select a template")));
      return;
    }
    const res = await PutApplyReservationTemplate({ template_data: [{ date, template_id: templateId }] }, true);
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(typeof res?.data === "string" ? res.data : t("Error applying templates")));
      return;
    }
    dispatch(showSuccess(t("Templates added to queue successfully")));
    onApplied();
  }

  async function generateSegments() {
    if (!startTime || !endTime) {
      dispatch(showError(t("Enter a start and end time")));
      return;
    }
    const res = await PostReservationSegmentConfig(
      {
        date,
        start_time: startTime,
        end_time: endTime,
        interval_sections: [{ start_time: startTime, end_time: endTime, interval }],
      },
      true,
    );
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error generating segments")));
      return;
    }
    dispatch(showSuccess(t("Segments generated successfully")));
    onGenerated();
  }

  return (
    <Sheet
      open={open}
      closable
      size="small"
      overflow
      title={moment(date).format("dddd, MMMM D")}
      onCancel={onClose}
      cancelText={t("Cancel")}
      okText={mode === "template" ? t("Apply") : t("Generate")}
      okDisabled={mode === "template" && !templateId}
      onOk={() => void (mode === "template" ? applyTemplate() : generateSegments())}
    >
      <div className="reservation-calendar-setup">
        <div className="reservation-calendar-setup-modes">
          <ButtonGroup
            fullWidth
            startKey={mode}
            onButtonClick={key => {
              if (key === "template" || key === "generate") {
                setMode(key);
              }
            }}
            buttons={[
              { key: "template", label: t("Apply template") },
              { key: "generate", label: t("Generate segments") },
            ]}
          />
        </div>
        {mode === "template" ? (
          <SelectNew
            id="apply-template"
            label={t("Template")}
            value={templateId}
            onChange={value => setTemplateId(Number(value))}
            options={templates.map(template => ({ id: template.id, label: template.name }))}
            placeholder={t("Select Template")}
          />
        ) : (
          <div className="reservation-calendar-setup-times">
            <TimePick size="large" value={startTime} onChange={time => setStartTime(time ?? "")} label={t("Start Time")} />
            <TimePick size="large" value={endTime} onChange={time => setEndTime(time ?? "")} label={t("End Time")} />
            <SelectNew
              id="interval"
              label={t("Interval (minutes)")}
              value={interval}
              onChange={value => setInterval(Number(value))}
              options={intervalOptions.map(option => ({ id: option, label: option }))}
            />
          </div>
        )}
      </div>
    </Sheet>
  );
}
